import { links } from '../links'
import type { Benefit, CapModule } from './de'

/**
 * Englische Fassung der CAP-Landingpage.
 *
 * TODO (Kunde): Übersetzung aus dem Deutschen – bitte fachlich und
 * terminologisch prüfen. Branchenbegriffe (Open Access, Wholesale, Wholebuy,
 * Footprint, Reseller, Supplier) sind bewusst unübersetzt geblieben, weil sie
 * im englischsprachigen Telekommunikationsmarkt genauso verwendet werden.
 */

export const hero = {
  title: ['The standard', 'for cooperation.'],
  lead:
    'CAP GO, FLOW and SCALE are three modules for different starting points, ' +
    'process requirements and levels of integration.',
}

export const openAccess = {
  eyebrow: 'Open Access with CAP',
  title: ['Build wholesale and wholebuy capability', 'in a structured way.'],
  steps: [
    'CAP is the platform for Open Access. It connects suppliers and resellers and creates standardised access, processes and integrations for working together in wholesale and wholebuy.',
    'Suppliers make networks, footprints and wholesale products available to resellers. Resellers open up further partner networks, regions and customer potential.',
    'CAP GO, FLOW and SCALE are three modules for different starting points, process requirements and levels of integration.',
  ],
  poles: { left: 'Reseller', right: 'Supplier' },
}

export const audiences = {
  eyebrow: 'Straight to the information you need',
  items: [
    {
      id: 'reseller',
      role: 'Reseller',
      title: ['More availability.', 'More offers.', 'Faster sales.'],
      body: 'Find out how CAP opens up additional footprints, automates wholebuy processes and integrates several suppliers into your system landscape.',
    },
    {
      id: 'supplier',
      role: 'Supplier',
      title: ['Fill your networks faster.', 'Activate resellers more easily.'],
      body: 'Find out how CAP makes your infrastructure wholesale-ready, connects resellers in a standardised way and integrates existing systems for complex wholesale setups.',
    },
  ],
}

export const modulesIntro = {
  title: 'Three modules.',
  subtitle: 'One entry point for every setup.',
}

export const modules: CapModule[] = [
  {
    id: 'cap-go',
    eyebrow: 'CAP GO',
    label: 'GO',
    title: ['Enter Open Access', 'with full', 'control.'],
    body: 'CAP GO makes networks, footprints and products available through a guided portal start. Suppliers reach additional resellers and resellers open up new markets – without setting up an integration project of their own first.',
    bullets: ['Guided portal start', 'Open up new footprints', 'Start without a separate project'],
    align: 'left',
  },
  {
    id: 'cap-flow',
    eyebrow: 'CAP FLOW',
    label: 'FLOW',
    title: ['Automate wholesale', 'and wholebuy', 'processes', 'reliably.'],
    body: 'CAP FLOW automates recurring transactions between suppliers and resellers. Orders, provisioning, changes and fault clearance run through defined process interfaces – for dependable wholesale operations.',
    bullets: ['Reduce manual work', 'Speed up status updates', 'Safeguard delivery quality'],
    align: 'right',
  },
  {
    id: 'cap-scale',
    eyebrow: 'CAP SCALE',
    label: 'SCALE',
    title: ['Integrate complex', 'system', 'landscapes.'],
    body: 'CAP SCALE connects existing systems, APIs and data models within a robust IT architecture. Different partners and product worlds can be integrated without starting a separate IT project for every new partner.',
    bullets: ['Make systems interoperable', 'Bundle partner complexity', 'Cut IT effort per partner'],
    align: 'left',
  },
]

export const together = {
  eyebrow: 'CAP for resellers and suppliers',
  title: ['CAP as the shared standard.', 'Your own connection, one common platform.'],
  columns: [
    {
      role: 'Reseller',
      cta: { label: 'Get in touch', href: links.contactReseller },
      benefits: [
        { text: 'Open up additional footprints and markets', strong: 'additional footprints' },
        { text: 'Automate recurring wholebuy processes', strong: 'wholebuy processes' },
        { text: 'Safeguard delivery quality and speed up status updates to end customers', strong: 'status updates to end customers' },
        { text: 'Integrate more suppliers with less IT effort', strong: 'less IT effort' },
      ] satisfies Benefit[],
    },
    {
      role: 'Supplier',
      cta: { label: 'Get in touch', href: links.contactSupplier },
      benefits: [
        { text: 'Market networks and wholesale products faster', strong: 'faster' },
        { text: 'Connect resellers in a standardised way', strong: 'resellers' },
        { text: 'Make existing systems wholesale-ready', strong: 'wholesale-ready' },
        { text: 'Run wholesale processes reliably', strong: 'wholesale processes' },
      ] satisfies Benefit[],
    },
  ],
}

export const faq = {
  title: 'FAQs',
  items: [
    {
      question: 'How do we get started with CAP?',
      answer:
        'That depends on your market role, your existing system landscape and the level of automation you want. The connection can be made through a guided portal access, a common market interface such as S/PRI, or an individual system and API integration.',
    },
    // TODO (Kunde): Die folgenden vier Antworten liegen im Entwurf nicht vor;
    // die deutschen Fassungen sind abgeleitet, diese sind deren Übersetzung.
    {
      question: 'Does every new partner need its own technical integration?',
      answer:
        'No. CAP provides standardised access and processes that apply to every connected partner. With CAP SCALE, existing systems, APIs and data models are integrated once – new partners are then connected through the shared platform, without a separate IT project for each one.',
    },
    {
      question: 'Which processes can be handled through CAP?',
      answer:
        'CAP FLOW covers the recurring transactions between suppliers and resellers: orders, provisioning, changes and fault clearance. They run through defined process interfaces, so status updates come back reliably and traceably.',
    },
    {
      question: 'Can we keep using our existing systems and interfaces?',
      answer:
        'Yes. CAP SCALE connects existing systems, APIs and data models within a robust IT architecture. Common market interfaces such as S/PRI are supported, as are individual system and API integrations.',
    },
    {
      question: 'Which CAP module fits our requirements?',
      answer:
        'It depends on your starting point: CAP GO for a guided start without an integration project of your own, CAP FLOW to automate recurring wholesale and wholebuy processes, and CAP SCALE to integrate complex system landscapes. We will look at your situation together and determine the next step.',
    },
  ],
}

export const finalCta = {
  eyebrow: 'CAP',
  title: ['Which CAP module', 'fits your requirements?'],
  body: 'We will look at your starting point together and determine the next step with CAP GO, FLOW or SCALE.',
  cta: { label: 'Get in touch', href: links.contactCap },
}

export const footerLinks = [
  { label: 'Carrier Hub', href: links.carrierhub },
  { label: 'Imprint', href: links.imprint },
  { label: 'Privacy', href: links.privacy },
]

export const ui = {
  skipToContent: 'Skip to content',
  toAudiences: 'To the information for resellers and suppliers',
}
